import React, { useCallback, useMemo, useState } from "react";
import {
  Box,
  BulkActionsBar,
  Button,
  DataTableGroupByDropdown,
  DataTableToolbar,
  DataTableToolbarActions,
  DataTableToolbarLeft,
  Icon,
  KanbanBoard,
  KanbanColumn,
  KanbanColumnSkeleton,
  MenuItem,
  Separator,
  Text,
  ToolbarAction,
  type BulkAction,
  type DragResult,
  type IconName,
  type SelectAllGroup,
} from "@salestrak/ui";
import { EntityTag } from "#app/components/entity";
import { FilterToggle } from "#app/components/filters/filter-toggle";
import { CurrentUserButton } from "#app/components/filters/current-user-button";
import { ExpandableSearch } from "@salestrak/ui";
import { IssueCard } from "#app/components/issue-card";
import { useIssueView } from "#app/components/issue-view";
import type { IssueRow, SortDirection } from "#app/lib/data";
import { useKanbanMutations } from "#app/hooks/kanban/use-kanban-mutations";
import { useBoardSelection } from "#app/hooks/kanban/use-board-selection";
import { useIssueListMutations } from "#app/hooks/issue-list/use-issue-list-mutations";
import { useRefetchIssueGroups } from "#app/hooks/issue-list/use-refetch-issue-groups";
import {
  BulkAssigneeContent,
  BulkStageContent,
} from "#app/components/issues/issue-bulk-actions";
import { IssueMenu } from "#app/components/issue-actions/issue-menu";
import { useResetOnChange } from "#app/lib/hooks/use-reset-on-change";
import { kanbanBoardSubscriptions } from "#app/hooks/kanban/kanban-subscriptions";
import { useRegisterSubscriptions } from "#app/lib/subscriptions/subscription-provider";
import { useColumnIssues, COLUMN_PAGE_SIZE } from "#app/hooks/kanban/use-column-issues";
import { useIssueGroupCounts } from "#app/hooks/issue-list/use-issue-group-counts";
import { useIssueFilters } from "#app/lib/filters/issue-filter-context";
import {
  scopeWhere,
  scopeWorkflowId,
  scopeKey,
  defaultBoardGrouping,
  type IssueScope,
} from "#app/lib/filters/issue-scope";
import { useWorkflowStagesData } from "#app/lib/hooks/use-kanban-board-data";
import { useCurrentUserFilterToggle } from "#app/lib/hooks/use-current-user-filter-toggle";
import { useAppNavigate } from "#app/lib/hooks/use-app-navigate";
import { usePermission } from "#app/lib/hooks/use-permission";
import { useAutomateStage } from "#app/hooks/automations/use-automate-stage";
import { AutomateStageMenuItem } from "#app/components/automations/automate-stage-menu-item";
import { useAppData, type WorkspaceWorkflow as Workflow } from "#app/lib/data";
import { useUserPool, useTeamPool } from "#app/components/entity-selectors";
import { canEdit } from "#app/lib/utils/access-level";
import {
  applyCurrentUserFilter,
  combineWhereConditions,
  buildSearchWhere,
} from "#app/lib/query-utils";
import { toastMutationError } from "#app/lib/utils/toast-errors";
import {
  useAnalytics,
  AnalyticsContext,
  IssueEvent,
  KanbanEvent,
  AnalyticsView,
  useTrackSearch,
} from "#app/lib/analytics";
import { AGE_STATUS_CONFIG, type AgeStatus } from "#app/lib/age-status";
import { ENTITY_KIND_ICON, iconSourceToName } from "#app/lib/entity-visuals";
import {
  buildGroupWhere,
  ISSUE_GROUP_SERVER_KEY,
  AGE_GROUPS,
  UNASSIGNED_DIMENSION_LABEL,
} from "#app/components/issues/issue-grouping";
import {
  buildBoardColumns,
  type BoardGroupDimension,
  type BoardColumnSource,
  type BuiltBoardColumn,
} from "#app/components/issues/board-columns";

/**
 * The issue Kanban for any scope. Columns are grouped by a chosen dimension
 * picked via the toolbar's group-by pill; each column self-fetches its own
 * windowed page of issues (`useColumnIssues`, fed the dimension's
 * `buildGroupWhere` selector) and feeds the library `KanbanBoard` primitive.
 *
 * A single workflow groups by `stage` (the only droppable dimension — drag
 * reorders/moves stages via `useKanbanMutations`, with realtime from
 * `kanbanBoardSubscriptions` on the shared subscription registry). A team or member spans many workflows, so it
 * groups by `workflow` and the board is read-only board-wide (`isDropDisabled`,
 * each column veils its body). The scope flows in as a prop; everything
 * stage/workflow-specific is gated on `scopeWorkflowId(scope)` being defined.
 */

const SEARCH_FIELDS = ["name", "description"];

// Manual board order — the drag-set sortOrder. Stable module reference so columns
// don't re-subscribe on every render.
const ORDER_BY: Record<string, unknown> = { sortOrder: 1 };

type Stage = ReturnType<typeof useWorkflowStagesData>["stages"][number];
type UserItem = ReturnType<typeof useUserPool>["pool"]["items"][number];
type TeamItem = ReturnType<typeof useTeamPool>["pool"]["items"][number];

// A column's header payload — resolved by dimension. `null` for the synthetic
// unassigned bucket; the age dimension carries its status key as a string.
type BoardColumnData = Stage | Workflow | UserItem | TeamItem | string;
type BoardColumn = BuiltBoardColumn<BoardColumnData>;

const ALL_GROUP_BY_LABELS: Record<BoardGroupDimension, string> = {
  stage: "Stage",
  workflow: "Workflow",
  assignee: "Assignee",
  team: "Team",
  age: "Age",
};

const ALL_GROUP_BY_ICONS: Partial<Record<string, IconName>> = {
  stage: "circle",
  workflow: ENTITY_KIND_ICON.workflow,
  assignee: "users",
  team: ENTITY_KIND_ICON.team,
  age: "clock",
};

// The dimensions offered per scope. A single workflow groups by stage; a
// cross-workflow scope (team/member) groups by workflow instead.
const WORKFLOW_SCOPE_DIMENSIONS: BoardGroupDimension[] = [
  "stage",
  "assignee",
  "team",
  "age",
];
const CROSS_WORKFLOW_DIMENSIONS: BoardGroupDimension[] = [
  "workflow",
  "assignee",
  "team",
  "age",
];

function pickLabels<T>(
  source: Record<string, T>,
  keys: BoardGroupDimension[],
): Record<string, T> {
  const out: Record<string, T> = {};
  for (const k of keys) if (source[k] !== undefined) out[k] = source[k];
  return out;
}

const getColumnId = (c: BoardColumn) => c.id;
const getItemId = (i: IssueRow) => i.issueId;
const getSortValue = (i: IssueRow) => i.sortOrder;
const isItemDragDisabled = (i: IssueRow) => !canEdit(i.viewerAccessLevel);
// Selection mirrors the drag/table gate: bulk actions only cover editable rows.
const isItemSelectable = (i: IssueRow) => canEdit(i.viewerAccessLevel);
const dragGroupLabel = (n: number) => `${n} project${n === 1 ? "" : "s"}`;
// Per-card checkboxes only — the board has no select-all, so bulk actions
// always run in ids mode.
const NO_SELECT_ALL_GROUPS: SelectAllGroup[] = [];
const estimateItemSize = (i: IssueRow | undefined) =>
  i?.address?.wholeAddress ? 125 : 109;

/**
 * The entity chip for a column header, resolved by dimension. Used by both the
 * expanded header and the collapsed rail so a column reads identically either way.
 */
function columnEntityNode(
  column: BoardColumn,
  display: "full" | "visual",
): React.ReactNode {
  if (column.isUnassigned) {
    return (
      <Text size="sm" variant="muted" truncate={1}>
        {column.label}
      </Text>
    );
  }
  switch (column.dimension) {
    case "stage": {
      const stage = column.data as Stage;
      return (
        <EntityTag
          entity={{
            kind: "stage",
            id: stage.stageId,
            stage,
            workflowId: stage.workflowId ?? undefined,
          }}
          display={display}
          weight="regular"
          interactive={false}
        />
      );
    }
    case "workflow": {
      const workflow = column.data as Workflow;
      return (
        <EntityTag
          entity={{
            kind: "workflow",
            id: workflow.workflowId,
            workflow: { name: workflow.name },
          }}
          display={display}
          interactive={false}
        />
      );
    }
    case "assignee": {
      const user = column.data as UserItem;
      return (
        <EntityTag
          entity={{
            kind: "user",
            id: user.id,
            user: { name: user.title, profileImage: user.avatar?.src ?? null },
          }}
          display={display}
          interactive={false}
        />
      );
    }
    case "team": {
      const team = column.data as TeamItem;
      return (
        <EntityTag
          entity={{
            kind: "team",
            id: team.id,
            team: {
              teamName: team.title,
              icon: {
                icon: iconSourceToName(team.avatar?.icon),
                color: team.avatar?.color ?? null,
              },
            },
          }}
          display={display}
          interactive={false}
        />
      );
    }
    case "age": {
      const info = AGE_STATUS_CONFIG[column.key as AgeStatus];
      if (!info) {
        return (
          <Text size="sm" variant="muted" truncate={1}>
            {column.label}
          </Text>
        );
      }
      return (
        <Box direction="row" align="center" gap="sm" overflow="hidden">
          {/* The config's semantic `background` token doubles as the icon color. */}
          <Icon name="clock" size="md" color={info.background} />
          <Text size="sm" weight="medium" truncate={1}>
            {info.label === "Disable age" ? "Disabled" : info.label}
          </Text>
        </Box>
      );
    }
  }
}

/**
 * One board column: mounts its own windowed `Issues` query for the column's
 * grouping selector and hands the page to the library `KanbanColumn`. Rendered
 * once per column via the board's `renderColumn` seam, so each column's hook is
 * its own component instance (hook rules hold). Dimension-neutral — the selector
 * comes from `buildGroupWhere`.
 */
function BoardColumnView({
  column,
  base,
  onlyCurrentUser,
}: {
  column: BoardColumn;
  base: Record<string, unknown> | undefined;
  onlyCurrentUser: boolean;
}) {
  const where = useMemo(() => {
    const selector = buildGroupWhere(column.dimension, column.key);
    return combineWhereConditions(base ? [selector, base] : [selector]) ?? selector;
  }, [base, column.dimension, column.key]);

  const { issues, dataOffset, total, loading, fetchPage, variablesKey } = useColumnIssues({
    where,
    onlyCurrentUser,
    orderBy: ORDER_BY,
  });

  return (
    <KanbanColumn<IssueRow, BoardColumn>
      column={column}
      data={{
        items: issues,
        dataOffset,
        total,
        loading,
        fetchPage,
        variablesKey,
        pageSize: COLUMN_PAGE_SIZE,
      }}
    />
  );
}

export function IssuesBoard({ scope }: { scope: IssueScope }) {
  const { track } = useAnalytics();
  const { push } = useAppNavigate();
  const workflowId = scopeWorkflowId(scope);
  // Board realtime: registers the workflow's kanban subscriptions with the
  // shared registry while the board is mounted (inert for cross-workflow
  // scopes, which render read-only).
  useRegisterSubscriptions(
    workflowId ? kanbanBoardSubscriptions(workflowId) : [],
  );
  const { filters, toggleFilterPanel } = useIssueFilters();
  const {
    isCurrentUserButtonSelected,
    isCurrentUserFilterApplied,
    handleCurrentUserPress,
    handleCurrentUserClear,
  } = useCurrentUserFilterToggle();
  const [searchValue, setSearchValue] = useState("");
  useTrackSearch(searchValue, () => ({
    context: AnalyticsContext.Issue,
    event: IssueEvent.Search,
    data: { view: AnalyticsView.Board, scope: scope.kind },
  }));

  // --- Grouping dimension + direction (the toolbar pill) ---
  const [groupBy, setGroupBy] = useState<BoardGroupDimension>(() =>
    defaultBoardGrouping(scope),
  );
  const [groupSortDirection, setGroupSortDirection] =
    useState<SortDirection>("asc");

  const dimensions = workflowId
    ? WORKFLOW_SCOPE_DIMENSIONS
    : CROSS_WORKFLOW_DIMENSIONS;
  const groupByLabels = useMemo(
    () => pickLabels(ALL_GROUP_BY_LABELS, dimensions),
    [dimensions],
  );
  const groupByIcons = useMemo(
    () => pickLabels(ALL_GROUP_BY_ICONS, dimensions),
    [dimensions],
  );

  const searchWhere = useMemo(
    () => buildSearchWhere(searchValue, SEARCH_FIELDS),
    [searchValue],
  );

  // The base filter shared by every column: scope + archived scoping (so the
  // non-stage groupings, whose selectors aren't implicitly scope-bound, don't
  // match issues outside the scope) plus panel filters + search.
  // `onlyCurrentUser` is applied per column by `useColumnIssues`, not folded here.
  const base = useMemo(() => {
    const conditions: Record<string, unknown>[] = [
      scopeWhere(scope),
      { archived: { eq: false } },
    ];
    if (filters.where) conditions.push(filters.where);
    if (searchWhere) conditions.push(searchWhere);
    return combineWhereConditions(conditions) ?? undefined;
  }, [scope, filters.where, searchWhere]);

  const { stages, loading } = useWorkflowStagesData(workflowId);
  const { pool: userPool } = useUserPool();
  const { pool: teamPool } = useTeamPool();
  const { workflows } = useAppData();

  // --- Column counts (only the unbounded dimensions need them to hide empties) ---
  const needsCounts = groupBy === "assignee" || groupBy === "team";
  const countsWhere = useMemo(
    () =>
      needsCounts
        ? applyCurrentUserFilter(base, isCurrentUserFilterApplied ?? false)
        : undefined,
    [needsCounts, base, isCurrentUserFilterApplied],
  );
  const { counts, ready: countsReady } = useIssueGroupCounts({
    where: countsWhere,
    groupBy: needsCounts ? ISSUE_GROUP_SERVER_KEY[groupBy] : undefined,
  });

  // --- Column sources per dimension ---
  const stageSources = useMemo<BoardColumnSource<BoardColumnData>[]>(
    () =>
      (stages as Stage[]).map((stage) => ({
        key: stage.stageId,
        label: stage.name,
        data: stage,
      })),
    [stages],
  );
  const workflowSources = useMemo<BoardColumnSource<BoardColumnData>[]>(
    () =>
      workflows.map((w) => ({
        key: w.workflowId,
        label: w.name ?? "Untitled workflow",
        data: w,
      })),
    [workflows],
  );
  const userSources = useMemo<BoardColumnSource<BoardColumnData>[]>(
    () => userPool.items.map((u) => ({ key: u.id, label: u.title, data: u })),
    [userPool.items],
  );
  const teamSources = useMemo<BoardColumnSource<BoardColumnData>[]>(
    () => teamPool.items.map((t) => ({ key: t.id, label: t.title, data: t })),
    [teamPool.items],
  );
  const ageSources = useMemo<BoardColumnSource<BoardColumnData>[]>(
    () => AGE_GROUPS.map((g) => ({ key: g.key, label: g.label, data: g.key })),
    [],
  );

  const columns = useMemo<BoardColumn[]>(() => {
    switch (groupBy) {
      case "stage":
        return buildBoardColumns({
          dimension: "stage",
          sources: stageSources,
          counts,
          sortDirection: groupSortDirection,
          unassignedLabel: UNASSIGNED_DIMENSION_LABEL.stage,
        });
      case "workflow":
        return buildBoardColumns({
          dimension: "workflow",
          sources: workflowSources,
          counts,
          sortDirection: groupSortDirection,
          unassignedLabel: UNASSIGNED_DIMENSION_LABEL.workflow,
        });
      case "age":
        return buildBoardColumns({
          dimension: "age",
          sources: ageSources,
          counts,
          sortDirection: groupSortDirection,
          unassignedLabel: UNASSIGNED_DIMENSION_LABEL.age,
        });
      case "assignee":
        return buildBoardColumns({
          dimension: "assignee",
          sources: userSources,
          counts,
          sortDirection: groupSortDirection,
          unassignedLabel: UNASSIGNED_DIMENSION_LABEL.assignee,
        });
      case "team":
        return buildBoardColumns({
          dimension: "team",
          sources: teamSources,
          counts,
          sortDirection: groupSortDirection,
          unassignedLabel: UNASSIGNED_DIMENSION_LABEL.team,
        });
    }
  }, [
    groupBy,
    stageSources,
    workflowSources,
    ageSources,
    userSources,
    teamSources,
    counts,
    groupSortDirection,
  ]);

  const { moveIssueToStage, reorderIssue, dropIssues } = useKanbanMutations();

  // --- Bulk selection ---
  // Flat id set + accumulated row map (columns page and evict). Cleared when
  // the board's identity changes — scope or grouping — AND when filters or
  // search change: a group drag lands every selected card, so a selection
  // that survived a filter would silently move cards the filter now hides.
  const { selectedIds, selectedRowData, toggle, clear } = useBoardSelection();
  useResetOnChange(
    `${scopeKey(scope)}::${groupBy}::${JSON.stringify(filters.where ?? null)}::${searchValue}`,
    clear,
  );

  const handleDragEnd = useCallback(
    async (result: DragResult<IssueRow, BoardColumn>) => {
      // Drops are only meaningful when grouped by stage; the library skips this
      // callback for drop-disabled groupings, but guard defensively.
      if (groupBy !== "stage") return;
      const {
        item,
        fromColumn,
        toColumn,
        position,
        upperNeighborId,
        lowerNeighborId,
        upperSortValue,
        lowerSortValue,
        groupIds,
      } = result;
      const fromStageId = fromColumn.key;
      const toStageId = toColumn.key;
      const upperNeighborKey = upperNeighborId != null ? String(upperNeighborId) : undefined;
      const lowerNeighborKey = lowerNeighborId != null ? String(lowerNeighborId) : undefined;

      // A group drag lands the whole selection contiguously at the drop slot.
      // Rows resolve from the selection map (the active card straight from the
      // result), ordered by current sortOrder so the block keeps its relative
      // order. Selection is kept after the drop — the wash follows the cards.
      if (groupIds && groupIds.length > 1) {
        const rows = groupIds
          .map((id) =>
            String(id) === item.issueId
              ? item
              : selectedRowData.get(String(id)),
          )
          .filter((r): r is IssueRow => r != null)
          .sort((a, b) => a.sortOrder - b.sortOrder);
        const anyStageChange = rows.some((r) => r.stageId !== toStageId);
        try {
          const moved = await dropIssues({
            rows,
            toStageId,
            upperNeighborKey,
            upperSortValue,
            lowerSortValue,
          });
          // Selection outlives the drop, so refresh each kept row with its
          // server-confirmed stage/sort — the next group drag derives every
          // row's fromStageId from this map, and a stale stageId would remove
          // the card from a column it already left.
          for (const row of moved) {
            if (selectedRowData.has(row.issueId)) {
              selectedRowData.set(row.issueId, row);
            }
          }
          track({
            context: AnalyticsContext.Kanban,
            event: anyStageChange
              ? KanbanEvent.DragMoveStage
              : KanbanEvent.DragReorder,
            data: { count: rows.length },
          });
        } catch (error) {
          toastMutationError(error, "Failed to move projects");
        }
        return;
      }

      try {
        if (fromStageId !== toStageId) {
          await moveIssueToStage({
            issue: item,
            fromStageId,
            toStageId,
            newSortOrder: position,
            upperNeighborKey,
          });
          track({ context: AnalyticsContext.Kanban, event: KanbanEvent.DragMoveStage });
        } else {
          await reorderIssue({
            issue: item,
            stageId: toStageId,
            upperNeighborKey,
            lowerNeighborKey,
            newSortOrder: position,
          });
          track({ context: AnalyticsContext.Kanban, event: KanbanEvent.DragReorder });
        }
      } catch (error) {
        toastMutationError(error, "Failed to move project");
      }
    },
    [groupBy, moveIssueToStage, reorderIssue, dropIssues, selectedRowData, track],
  );

  const { openCreateIssue, openIssue } = useIssueView();

  // Create gate — both the toolbar "Add project" and the per-column footer require
  // role `create.items` AND edit-or-better access on this workflow. Only a
  // single-workflow scope can create (you must know which workflow to add to).
  const workflow = workflows.find((w) => w.workflowId === workflowId);
  const canCreateItems = usePermission("create.items");
  const canAddProject =
    !!workflowId && canCreateItems && canEdit(workflow?.viewerAccessLevel);

  // "Automate stage" on a stage group — shared gate.
  const { canAutomateStage } = useAutomateStage(workflowId);

  const handleAddProject = useCallback(
    () => workflowId && openCreateIssue({ workflowId, source: "kanban_header" }),
    [workflowId, openCreateIssue],
  );

  const renderCard = useCallback(
    (
      issue: IssueRow,
      { isDragOverlay, isSelected }: { isDragOverlay: boolean; isSelected: boolean },
    ) => (
      <IssueCard
        issue={issue}
        isDragOverlay={isDragOverlay}
        isSelected={isSelected}
      />
    ),
    [],
  );

  // The card cluster's 'More' slot — the same menu the card already offers on
  // right-click, in its dropdown flavor (it renders its own trigger button).
  const cardActions = useCallback(
    (issue: IssueRow) => (
      <IssueMenu
        issue={issue}
        type="dropdown"
        readOnly={!canEdit(issue.viewerAccessLevel)}
      />
    ),
    [],
  );

  const handleCardPress = useCallback(
    (issue: IssueRow) => openIssue(issue),
    [openIssue],
  );

  // --- Bulk actions (the same popover bodies the list view mounts) ---
  const { bulk: bulkMutations } = useIssueListMutations();
  const refetchGroups = useRefetchIssueGroups();

  // Bulk change-stage only for a single workflow (a cross-workflow selection
  // has no single stage target). Bulk assignees works for any scope. Ids-only:
  // the board has no select-all, so `selectAllWhere` never applies.
  const bulkActions = useMemo<BulkAction<IssueRow>[]>(() => {
    const actions: BulkAction<IssueRow>[] = [];

    const wfId = workflowId;
    if (wfId && stages.length > 0) {
      actions.push({
        id: "change-stage",
        type: "popover",
        label: "Move to stage",
        icon: "layers",
        contentProps: { padding: "none" },
        content: ({ selectedRows, commit }) => (
          <BulkStageContent
            view={AnalyticsView.Board}
            scope={scope.kind}
            workflowId={selectedRows[0]?.workflowId ?? wfId}
            commit={commit}
            bulkMutations={bulkMutations}
            refetchGroups={refetchGroups}
            availableStages={stages}
            selectedCount={selectedRows.length}
          />
        ),
      });
    }

    actions.push({
      id: "set-assignees",
      type: "popover",
      label: "Assignees",
      icon: "users",
      contentProps: { padding: "none", width: 384 },
      content: ({ selectedRows }) => (
        <BulkAssigneeContent
          view={AnalyticsView.Board}
          scope={scope.kind}
          bulkMutations={bulkMutations}
          selectedRows={selectedRows}
        />
      ),
    });

    return actions;
  }, [workflowId, stages, scope.kind, bulkMutations, refetchGroups]);

  const renderColumn = useCallback(
    (column: BoardColumn) => (
      <BoardColumnView
        column={column}
        base={base}
        onlyCurrentUser={isCurrentUserFilterApplied}
      />
    ),
    [base, isCurrentUserFilterApplied],
  );

  const renderColumnHeader = useCallback(
    ({ column, count }: { column: BoardColumn; count: number }) => (
      <>
        {columnEntityNode(column, "full")}
        <Text size="sm" weight="medium" variant="muted">
          {count}
        </Text>
      </>
    ),
    [],
  );

  const renderCollapsedColumn = useCallback(
    ({ column, count }: { column: BoardColumn; count: number }) => (
      <Box direction="column" align="center" gap="sm">
        {columnEntityNode(column, "visual")}
        {/* Old Box `writingMode`/`whiteSpace` props have no successors — the
            vertical rail label is a class pair now. */}
        <Box className="[writing-mode:vertical-rl] whitespace-nowrap">
          <Text size="xs" weight="medium">
            {column.label}
          </Text>
        </Box>
        <Box className="[writing-mode:vertical-rl]">
          <Text size="xs" variant="muted">
            {count}
          </Text>
        </Box>
      </Box>
    ),
    [],
  );

  // Stage-only chrome: the per-column "Add project" footer and the "Edit stages"
  // overflow item only make sense when columns ARE stages.
  const renderColumnFooter = useMemo(
    () =>
      groupBy === "stage" && canAddProject
        ? (column: BoardColumn) => (
            <Button
              variant="ghost"
              size="sm"
              onClick={() =>
                workflowId &&
                openCreateIssue({
                  workflowId,
                  stageId: column.key,
                  source: "kanban_column",
                })
              }
            >
              <Icon name="plus" size="md" />
              <Text size="sm">Add project</Text>
            </Button>
          )
        : undefined,
    [groupBy, canAddProject, workflowId, openCreateIssue],
  );

  const columnMenuItems = useMemo(
    () =>
      groupBy === "stage"
        ? (column: BoardColumn) => (
            <>
              <MenuItem
                onClick={() =>
                  workflowId &&
                  push(`/settings/workflows/${workflowId}/stages/${column.key}`)
                }
              >
                <Icon name="settings" size="md" />
                <Text>Edit stages</Text>
              </MenuItem>
              {canAutomateStage && workflowId ? (
                <AutomateStageMenuItem
                  workflowId={workflowId}
                  stageId={column.key}
                  iconSize="md"
                />
              ) : null}
            </>
          )
        : undefined,
    [groupBy, workflowId, push, canAutomateStage],
  );

  const isDropDisabled = groupBy !== "stage";
  const dropDisabledOverlay = useMemo(
    () => (
      <Box
        align="center"
        gap="xs"
        padding="md"
        maxWidth={220}
      >
        <Icon name="lock" size="md" color="muted" />
        <Text size="xs" variant="muted" align="center">
          Switch to Stage grouping to reorder
        </Text>
      </Box>
    ),
    [],
  );

  const hasActiveFilters = filters.activeFilterCount > 0;

  if ((loading && stages.length === 0) || (needsCounts && !countsReady)) {
    return (
      <Box fill="flex" direction="row" gap="md" padding="md">
        {Array.from({ length: 4 }).map((_, i) => (
          <KanbanColumnSkeleton key={i} />
        ))}
      </Box>
    );
  }

  return (
    // `relative` anchors the floating bulk-actions bar.
    <Box direction="column" fill="flex" position="relative">
      {/* Same toolbar chrome as the widget surfaces (surface-chrome.tsx):
          page-default background with a bottom hairline. */}
      <Box background="background">
        <DataTableToolbar>
          <DataTableToolbarLeft>
            <DataTableGroupByDropdown
              groupByField={groupBy}
              setGroupByField={(field) => {
                setGroupBy(field as BoardGroupDimension);
                track({
                  context: AnalyticsContext.Issue,
                  event: IssueEvent.ChangeGrouping,
                  data: { view: AnalyticsView.Board, scope: scope.kind, dimension: field },
                });
              }}
              groupByLabels={groupByLabels}
              groupByIcons={groupByIcons}
              groupSortDirection={groupSortDirection}
              setGroupSortDirection={setGroupSortDirection}
            />
          </DataTableToolbarLeft>
          <DataTableToolbarActions>
            <CurrentUserButton
              selected={isCurrentUserButtonSelected}
              onClick={() => {
                handleCurrentUserPress();
                track({
                  context: AnalyticsContext.Issue,
                  event: IssueEvent.ToggleOnlyMe,
                  data: { view: AnalyticsView.Board, scope: scope.kind, enabled: true },
                });
              }}
              onClearClick={() => {
                handleCurrentUserClear();
                track({
                  context: AnalyticsContext.Issue,
                  event: IssueEvent.ToggleOnlyMe,
                  data: { view: AnalyticsView.Board, scope: scope.kind, enabled: false },
                });
              }}
              size="xs"
            />
            <FilterToggle
              size="xs"
              icon="filter"
              count={filters.activeFilterCount}
              selected={hasActiveFilters}
              onClick={toggleFilterPanel}
              onClear={filters.clearAll}
            />
            <ExpandableSearch
              onValueChange={setSearchValue}
              placeholder="Search projects..."
              size="xs"
            />
            {canAddProject ? (
              <Box direction="row" align="center" gap="sm">
                
                <Separator orientation="vertical" className="h-4" />
                <ToolbarAction icon="plus" label="Add project" onClick={handleAddProject} />
              </Box>
            ) : null}
          </DataTableToolbarActions>
        </DataTableToolbar>
        <Separator />
      </Box>
      <KanbanBoard<IssueRow, BoardColumn>
        columns={columns}
        getColumnId={getColumnId}
        getItemId={getItemId}
        getSortValue={getSortValue}
        renderColumn={renderColumn}
        estimateItemSize={estimateItemSize}
        isItemDragDisabled={isItemDragDisabled}
        isDropDisabled={isDropDisabled}
        dropDisabledOverlay={dropDisabledOverlay}
        onDragEnd={handleDragEnd}
        renderCard={renderCard}
        onCardClick={handleCardPress}
        renderColumnHeader={renderColumnHeader}
        renderCollapsedColumn={renderCollapsedColumn}
        renderColumnFooter={renderColumnFooter}
        columnMenuItems={columnMenuItems}
        selectedItemIds={selectedIds}
        onItemSelectedChange={toggle}
        isItemSelectable={isItemSelectable}
        cardActions={cardActions}
        dragGroupLabel={dragGroupLabel}
        emptyState={<Text variant="muted">No columns to show</Text>}
      />
      <BulkActionsBar
        actions={bulkActions}
        selectedIds={selectedIds}
        onClearSelection={clear}
        selectedRowData={selectedRowData}
        selectAllGroups={NO_SELECT_ALL_GROUPS}
      />
    </Box>
  );
}
